<template>
  <v-card id="beneficiary-card" flat rounded="0">
    <!-- header -->
    <v-card-title>
      <v-row>
        <v-col cols="7" sm="6" md="8" class="text-wrap">
          <h1 class="text-h2">{{ $t('L_policy_beneficiaries') }}</h1>
        </v-col>
        <v-col cols="5" sm="6" md="4" v-if="showUpdateBeneficiary" class="text-right pl-0">
          <OnexButton color="primary" @click="showUpdateBeneficiaries">
            <span class="pl-2 pr-2" v-if="$vuetify.display.xs">{{ $t('L_update') }}</span>
            <span class="pl-2 pr-2" v-else>{{ $t('L_update_beneficiaries') }}</span>
          </OnexButton>
        </v-col>
        <v-col cols="12" v-if="dateSigned !== ''">
          <p class="font-size--24 text-wrap font-weight-bold">
            {{ $t('L_last_updated_on') }}<span>{{ dateSigned }}</span>
          </p>
        </v-col>
      </v-row>
    </v-card-title>
    <v-divider />
    <v-row v-if="displayBeneficiaries" class="py-4">
      <!-- Primary Insured -->
      <v-col cols="12" sm="8" md="6" lg="6">
        <v-row v-if="pPrimaryBeneficiaries.length > 0" class="pl-4">
          <v-col cols="0" sm="1" md="1" lg="1" />
          <v-col cols="12" sm="11" md="11" lg="11" class="text-left">
            <h2 class="font-size--20">
              <span class="font-weight-regular">{{ $t('L_policy_insured') }}</span
              >&nbsp;<span class="font-weight-bold">{{ primaryInsured }}</span>
            </h2>
          </v-col>
        </v-row>
        <!-- Primary Primary Beneficiaries -->
        <v-row v-if="pPrimaryBeneficiaries.length > 0" class="pl-4">
          <v-col cols="0" sm="1" md="1" lg="1" class="pt-0 pb-0" />
          <v-col cols="6" sm="6" md="6" lg="6" text-left class="pt-0 pb-0">
            <span class="text-h4">
              {{ $t('L_policy_beneficiaries_primary') }}
            </span>
          </v-col>
          <v-col
            cols="6"
            sm="5"
            class="pt-0 pb-0 text-center pl-0 pr-0"
            :class="$vuetify.display.xsOnly ? 'pr-10' : 'pr-0'"
          >
            <span class="text-h4" v-if="displayPrimaryAllocation">
              {{ $t('L_policy_beneficiaries_allocation') }}
            </span>
          </v-col>
        </v-row>
        <v-row
          v-for="(beneficiary, index) in pPrimaryBeneficiaries"
          :key="index"
          class="font-size--14 pl-4"
          row
          wrap
        >
          <v-col cols="0" sm="1" md="1" lg="1" class="pt-0 pb-0" />
          <v-col cols="6" sm="6" md="6" lg="6" class="pt-0 pb-0 text-left">
            <span class="BeneficiaryName">
              {{ getFullNameForDisplayWithLocaleBC(beneficiary.fullName || '') }}
            </span>
          </v-col>
          <v-col cols="6" sm="5" md="5" lg="5" class="pt-0 pb-0 text-center">
            <span
              class="BeneficiaryName"
              v-if="beneficiary.origin === 'A' || beneficiary.origin === 'C'"
            >
              {{ beneficiary.beneficiaryPercent }} %
            </span>
          </v-col>
        </v-row>
        <!-- Primary Contingent Beneficiaries -->
        <v-row v-if="pContingentBeneficiaries.length > 0" class="pl-4">
          <v-col cols="0" sm="1" md="1" lg="1" class="pt-2 pb-0" />
          <v-col cols="6" sm="6" md="6" lg="6" class="pt-2 pb-0 text-left">
            <span class="text-h4">
              {{ $t('L_policy_beneficiaries_contingent') }}
            </span>
          </v-col>
          <v-col cols="6" sm="5" md="5" lg="5" class="pt-2 pb-0">
            <span class="BeneficiaryName" />
          </v-col>
        </v-row>
        <v-row
          v-for="(beneficiary, index) in pContingentBeneficiaries"
          :key="index"
          class="font-size--14 pl-4"
          row
          wrap
        >
          <v-col cols="0" sm="1" md="1" lg="1" class="pt-0 pb-0" />
          <v-col cols="6" sm="6" md="6" lg="6" class="text-left pt-0 pb-0">
            <span class="BeneficiaryName">
              {{ getFullNameForDisplayWithLocaleBC(beneficiary.fullName || '') }}
            </span>
          </v-col>
          <v-col cols="6" sm="5" md="5" lg="5" class="text-center pt-0 pb-0">
            <span
              class="BeneficiaryName"
              v-if="beneficiary.origin === 'A' || beneficiary.origin === 'C'"
            >
              {{ beneficiary.beneficiaryPercent }} %
            </span>
          </v-col>
        </v-row>
      </v-col>
      <!-- Spouse Insured -->
      <v-col cols="12" sm="8" md="6" lg="6">
        <v-row v-if="sPrimaryBeneficiaries.length > 0" class="pl-4">
          <v-col cols="0" sm="1" md="1" lg="1" class="pt-4 pb-0" />
          <v-col cols="12" sm="11" md="11" lg="11" class="pt-4 pb-0 text-left">
            <h2 class="font-size--20">
              <span class="font-weight-regular">{{ $t('L_policy_spouse_insured') }}</span
              >&nbsp;<span class="font-weight-bold">{{ spouse }}</span>
            </h2>
          </v-col>
        </v-row>
        <!-- Spouse Primary Beneficiaries -->
        <v-row v-if="sPrimaryBeneficiaries.length > 0" class="pl-4">
          <v-col cols="0" sm="1" md="1" lg="1" class="pt-2 pb-0" />
          <v-col cols="6" sm="6" md="6" lg="6" class="pt-2 pb-0">
            <h3 class="text-h4">
              {{ $t('L_policy_beneficiaries_primary') }}
            </h3>
          </v-col>
          <v-col cols="6" sm="5" md="5" lg="5" class="pt-2 pb-0 text-center pl-0 pr-0">
            <span class="text-h4" v-if="displaySpouseAllocation">
              {{ $t('L_policy_beneficiaries_allocation') }}
            </span>
          </v-col>
        </v-row>
        <v-row
          v-for="(beneficiary, index) in sPrimaryBeneficiaries"
          :key="index"
          class="font-size--14 pl-4"
          row
          wrap
        >
          <v-col cols="0" sm="1" md="1" lg="1" class="pt-0 pb-0" />
          <v-col cols="6" sm="6" md="6" lg="6" class="pt-0 pb-0 text-left">
            <span class="BeneficiaryName">
              {{ getFullNameForDisplayWithLocaleBC(beneficiary.fullName || '') }}
            </span>
          </v-col>
          <v-col cols="6" sm="5" md="5" lg="5" class="pt-0 pb-0 text-center">
            <span
              class="BeneficiaryName"
              v-if="beneficiary.origin === 'A' || beneficiary.origin === 'C'"
            >
              {{ beneficiary.beneficiaryPercent }} %
            </span>
          </v-col>
        </v-row>
        <!-- Spouse Contingent Beneficiaries -->
        <v-row v-if="sContingentBeneficiaries.length > 0" class="pl-4">
          <v-col cols="0" sm="1" md="1" lg="1" class="pt-2 pb-0" />
          <v-col cols="6" sm="6" md="6" lg="6" class="pt-2 pb-0 text-left">
            <h3 class="text-h4">
              {{ $t('L_policy_beneficiaries_contingent') }}
            </h3>
          </v-col>
          <v-col cols="6" sm="5" md="5" lg="5" class="pt-2 pb-0 text-center">
            <h3 class="BeneficiaryName"></h3>
          </v-col>
        </v-row>
        <v-row
          v-for="(beneficiary, index) in sContingentBeneficiaries"
          :key="index"
          class="font-size--14 pl-4"
          row
          wrap
        >
          <v-col cols="0" sm="1" md="1" lg="1" class="pt-0 pb-0" />
          <v-col cols="6" sm="6" md="6" lg="6" class="pt-0 pb-0 text-left">
            <span class="BeneficiaryName">
              {{ getFullNameForDisplayWithLocaleBC(beneficiary.fullName || '') }}
            </span>
          </v-col>
          <v-col cols="6" sm="5" md="5" lg="5" class="pt-0 pb-0 text-center">
            <span
              class="BeneficiaryName"
              v-if="beneficiary.origin === 'A' || beneficiary.origin === 'C'"
            >
              {{ beneficiary.beneficiaryPercent }} %
            </span>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- TODO: refactor into own component? -->
    <!-- When Update Beneficiaries button is clicked and there's beneficiaryHasOriginVorTWithIrrevocable-->
    <!-- v-model="showImportantInformationDialog" -->
    <v-dialog
      max-width="700"
      :persistent="true"
      :fullscreen="$vuetify.display.xs"
      v-model="showImportantInformationDialog"
    >
      <v-card class="pa-4">
        <v-row class="py-0">
          <v-col class="bluebar py-0"></v-col>
        </v-row>
        <v-row justify="end">
          <v-col cols="auto" class="text-right">
            <v-icon
              @click="showImportantInformationDialog = false"
              color="#000000"
              :icon="mdiClose"
            />
          </v-col>
        </v-row>
        <v-row class="mx-4 px-2">
          <v-col cols="12" class="font-weight-bold text-center mb-4">
            <v-icon color="#3399ff" class="mx-3" :icon="mdiInformationOutline" />
            <span>{{ $t('L_important_information_about_irrevocable_beneficiary_change') }}</span>
          </v-col>
          <v-col cols="12" class="text-left">
            <p class="mb-4">{{ $t('L_beneficiary_changes_cannot_be_completed_online_because') }}</p>
            <p class="mb-4">
              {{ $t('L_please_request_a_paper_beneficiary_designation_form')
              }}<span>{{ customerServicePhoneNum }}</span>
            </p>
          </v-col>
        </v-row>
        <v-card-actions class="justify-center mb-4">
          <OnexButton
            id="closeErrorDialogShownForSubmit"
            class="px-4"
            color="primary"
            @click="closeDialogForVorTirrevocable"
          >
            <h3 class="text-h3 font-weight-bold">
              {{ $t('L_ok_got_it') }}
            </h3>
          </OnexButton>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { openInNewWindow } from '@/mixins/openNewWindow';
import bff from '@/shared/api/bff';
import { mapActions, mapState, mapStores } from 'pinia';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useGlobalStore } from '@/stores/global/global.store';
import { format, parseISO } from 'date-fns';
import DynatraceClient from '@/plugins/dynatracePlugin';
import { FEATURE_FLAGS } from '@/shared/models/constants.models';
import { Translations } from '@/i18n/models';
import type { IPolicyStoreBeneficiaryInfo } from '@/modules/policy/store/policyStore.model';
import { mdiClose, mdiInformationOutline } from '@mdi/js';
import { OnexButton } from '@primerica/onex-ui-lib';
import { RouteNames } from '@/router/routeNames';

const componentOptions: ComponentOptions = {
  name: 'BeneficiaryCard',
  components: {
    OnexButton,
  },
  mixins: [openInNewWindow],
  data: () => ({
    showImportantInformationDialog: false,
    Translations,
    mdiInformationOutline,
    mdiClose,
  }),
  methods: {
    ...mapActions(usePolicyStore, ['getBeneficiaries']),
    async helpAction() {
      const _getRightAnswersHelpUrl = await bff.getRightAnswersHelpUrl();
      const helpUrl = _getRightAnswersHelpUrl.data?.URL || '';
      const url =
        'https://primerica.rightanswers.com/portal/app/portlets/results/viewsolution.jsp?solutionid=210617092059223&';
      // Current data returned as we are grabbing everything after ss? and appending to our url above which should end in '&'
      //  "URL":"https://primericastg.rightanswers.com/portal/ss?companycode=primerica&tabid=2&authID=primericaJwt&group=Client Portal&jwtoken=eyJ0eXAiOiJKV1QiLA0KICJhbGc"
      const finalDestination = encodeURI(url + helpUrl?.split('ss?')[1]);
      // TODO: use openInNewWindow mixin
      const win2 = window.open(helpUrl, 'RADashboardWinName')?.location ?? '';
      if (win2) {
        (win2 as unknown as Window).location.href = finalDestination;
      }
    },
    getFullNameForDisplayWithLocaleBC(fullName: string) {
      let retFullName = fullName;
      if (this.locale === 'fr_CA' || this.locale === 'es_US') {
        if (fullName === 'Estate') {
          retFullName = this.$t('L_estate');
        } else if (fullName === 'All Children Equally') {
          retFullName = this.$t('L_all_children_equally');
        } else {
          return retFullName;
        }
      }
      return retFullName;
    },
    async showUpdateBeneficiaries() {
      try {
        const i = DynatraceClient.enterAction('updateBeneficiaries_start');
        DynatraceClient.leaveAction(i);
      } catch (_error) {
        /* empty */
      }

      if (this.beneficiaryHasOriginVorTWithIrrevocable) {
        this.showImportantInformationDialog = true;
      } else {
        this.policyStore.isBeneficiaryDataDirty = false;
        // TODO: use routing
        // Start Update Beneficiary Process, See PolicyDetails.vue
        this.policyStore.beneficiaryStep = 1;
        await this.$router.push({
          name: RouteNames.BENEFICIARY_STEP,
        });
      }
    },
    closeDialogForVorTirrevocable() {
      if (this.beneficiaryHasOriginVorTWithIrrevocable) {
        this.showImportantInformationDialog = false;
      }
    },
  },
  computed: {
    ...mapStores(usePolicyStore),
    ...mapState(useGlobalStore, { locale: 'getLocale' }),
    ...mapState(usePolicyStore, {
      isPending: 'isPending',
      policy: 'currentPolicy',
      beneficiaries: 'beneficiaries',
      beneficiaryHasOriginVorTWithIrrevocable: 'beneficiaryHasOriginVorTWithIrrevocable',
      beneficiaryHasOriginVorT: 'beneficiaryHasOriginVorT',
    }),
    ...mapState(useGlobalStore, ['containsFeature', 'features']),
    dateSigned() {
      if (this.beneficiaries.length > 0) {
        const sdate = this.beneficiaries[0].dateSigned;
        if (sdate === null || sdate === '') {
          return '';
        }
        if (this.locale === 'fr_CA') {
          return format(parseISO(sdate), 'dd/MM/yyyy');
        }
        return format(parseISO(sdate), 'MM/dd/yyyy');
      }
      return '';
    },
    customerServicePhoneNum() {
      if (this.locale === 'en_US') {
        return '(800) 257-4725';
      } else if (this.locale === 'en_CA') {
        return '(800) 387-7876';
      } else if (this.locale === 'fr_CA') {
        return '(800) 265-4804';
      }
      return '(800) 257-4725';
    },
    showUpdateBeneficiary() {
      return (
        !this.isPending &&
        this.containsFeature(FEATURE_FLAGS.BENEFICIARIES_EDIT) &&
        this.policy.allowEditBeneficiary
      );
    },
    displayBeneficiaries() {
      return (
        this.containsFeature(FEATURE_FLAGS.BENEFICIARIES) &&
        this.beneficiaries &&
        this.beneficiaries?.length > 0
      );
    },
    pPrimaryBeneficiaries(): IPolicyStoreBeneficiaryInfo[] {
      return this.beneficiaries?.filter(
        (beneficiary: IPolicyStoreBeneficiaryInfo) => String(beneficiary.beneficiaryType) === 'P'
      );
    },
    pContingentBeneficiaries(): IPolicyStoreBeneficiaryInfo[] {
      return this.beneficiaries?.filter(
        (beneficiary: IPolicyStoreBeneficiaryInfo) => String(beneficiary.beneficiaryType) === 'C'
      );
    },
    sPrimaryBeneficiaries(): IPolicyStoreBeneficiaryInfo[] {
      return this.beneficiaries?.filter(
        (beneficiary: IPolicyStoreBeneficiaryInfo) => String(beneficiary.beneficiaryType) === 'S'
      );
    },
    sContingentBeneficiaries(): IPolicyStoreBeneficiaryInfo[] {
      return this.beneficiaries?.filter(
        (beneficiary: IPolicyStoreBeneficiaryInfo) => String(beneficiary.beneficiaryType) === 'H'
      );
    },
    primaryInsured() {
      const firstName = this.policy?.primaryInsured?.firstName || '';
      const lastName = this.policy?.primaryInsured?.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      return fullName !== '' ? fullName : '';
    },
    spouse() {
      if (this.policy?.data?.spouseLastName) {
        return `${this.policy?.data?.spouseFirstName} ${this.policy?.data?.spouseLastName}`;
      }
      return this.$t('L_policy_beneficiaries_spouse');
    },
    displaySpouseAllocation() {
      let display = false;

      this.sPrimaryBeneficiaries?.forEach((bene: IPolicyStoreBeneficiaryInfo) => {
        if (bene.origin === 'A' || bene.origin === 'C') {
          display = true;
        }
      });

      this.sContingentBeneficiaries?.forEach((bene: IPolicyStoreBeneficiaryInfo) => {
        if (bene.origin === 'A' || bene.origin === 'C') {
          display = true;
        }
      });
      return display;
    },
    displayPrimaryAllocation() {
      let display = false;

      this.pPrimaryBeneficiaries?.forEach((bene: IPolicyStoreBeneficiaryInfo) => {
        if (bene.origin === 'A' || bene.origin === 'C') {
          display = true;
        }
      });

      this.pContingentBeneficiaries?.forEach((bene: IPolicyStoreBeneficiaryInfo) => {
        if (bene.origin === 'A' || bene.origin === 'C') {
          display = true;
        }
      });
      return display;
    },
  },
  async mounted() {
    if (parseInt(this.policy.contractId, 10) > 0) {
      await this.getBeneficiaries(this.policy.contractId);
    }
  },
  watch: {
    policy(newData, oldData) {
      if (newData !== oldData && parseInt(this.policy.contractId, 10) > 0) {
        this.getBeneficiaries(this.policy.contractId);
      }
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss">
.cssDateSigned {
  font-weight: 600;
}

.bluebar {
  border-top: 8px solid #3399ff;
}

#beneficiary-card {
  .move-right {
    float: right;
  }

  .effective-as-of {
    color: #424242;
  }

  .insured-name-label {
    color: #333333;
  }

  .insured-name {
    color: #333333;
  }

  .Primary {
    color: #333333;
  }

  .Contingent {
    color: #333333;
  }

  .Allocation {
    color: #333333;
  }

  .BeneficiaryName {
    color: #424242;
  }
}
</style>
